import Link from 'next/link'
import { ReactNode, ReactElement, useEffect, useRef, useState, cloneElement } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { colors, fonts, media, entranceAnimation } from 'shared/lib'
import { RaffleDetail, RaffleImage } from 'shared/types/raffles'
import { t } from 'localization'
import { formatRaffleDate } from './raffleUtils'
import { ChevronDown } from 'shared/components/Icons/SVGIcons/ChevronDown'

const TP = 'shared.components.Raffles.RaffleDetailPage'

/* The global stylesheet (src/shared/styles/global.css) sets
   `html, body { overflow-x: hidden }`, which silently makes the
   document the scroll container and disables position:sticky on
   descendants. The prototype relies on viewport scrolling to keep
   the left and right sidebars pinned; override the global for this
   page only so sticky works as designed.

   Also make the nav scroll away (position: static) to match the
   standard PDP behavior across the site. */
const StickyScrollFix = createGlobalStyle`
  html, body {
    overflow-x: visible;
  }

  [class*="Navigation__ScrollContainer"] {
    position: static !important;
  }
`

interface IRaffleDetailPageProps {
  raffle: RaffleDetail
  panel?: ReactNode
}

const renderImage = (
  image: RaffleImage,
  index: number,
  productName: string,
) => {
  if (image.kind === 'campaign') {
    return (
      <CampaignImageWrap key={image.src} data-qa="raffle-detail-image">
        <CampaignImageInner>
          <img
            src={image.src}
            alt={`${productName} - ${t(`${TP}.imageAlt`, 'Image')} ${index + 1}`}
          />
        </CampaignImageInner>
      </CampaignImageWrap>
    )
  }
  return (
    <ProductImageItem key={image.src} data-qa="raffle-detail-image">
      <img
        src={image.src}
        alt={`${productName} - ${t(`${TP}.imageAlt`, 'Image')} ${index + 1}`}
      />
    </ProductImageItem>
  )
}

export const RaffleDetailPage = ({ raffle, panel }: IRaffleDetailPageProps) => {
  const {
    brand,
    productName,
    rafflePrice,
    currency,
    sku,
    colorway,
    releaseDate,
    mainImage,
    images,
  } = raffle

  const allImages: RaffleImage[] = [mainImage, ...images]

  const formattedPrice = `${t(`${TP}.rafflePrice`, 'Price:')} ${
    currency === 'USD' ? '$' : currency === 'JPY' ? '¥' : currency
  }${rafflePrice.toLocaleString()}`

  const [isCollapsed, setIsCollapsed] = useState(true)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth >= 1100) return

      const scrollTop = window.scrollY
      const sentinelTop = sentinelRef.current?.offsetTop || 0
      const isLanded = scrollTop + window.innerHeight - 16 >= sentinelTop

      if (isCollapsed && isLanded) {
        setIsCollapsed(false)
      } else if (!isCollapsed && !isLanded) {
        setIsCollapsed(true)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isCollapsed])

  return (
    <PageWrapper data-qa="raffle-detail-page">
      <StickyScrollFix />
      <Breadcrumbs data-qa="raffle-detail-breadcrumbs">
        <Link href="/" passHref legacyBehavior>
          <BreadcrumbLink>{t(`${TP}.breadcrumbHome`, 'Flight Club')}</BreadcrumbLink>
        </Link>
        <Separator>/</Separator>
        <Link href="/drawings" passHref legacyBehavior>
          <BreadcrumbLink>{t(`${TP}.breadcrumbRaffles`, 'Drawings')}</BreadcrumbLink>
        </Link>
        <Separator>/</Separator>
        <BreadcrumbCurrent>{productName}</BreadcrumbCurrent>
      </Breadcrumbs>

      <MobileLayout>
        <MobileTitleComponent>
          <MobileTitleTexts>
            <MobileBrand>{brand}</MobileBrand>
            <MobileProductName>{productName}</MobileProductName>
          </MobileTitleTexts>
          <MobilePrice>{formattedPrice}</MobilePrice>
        </MobileTitleComponent>

        <MobileImagesSection>
          {allImages.map((image, i) => renderImage(image, i, productName))}
        </MobileImagesSection>

        <MobileProductInfo data-qa="raffle-detail-product-info">
          <MobileInfoLine data-qa="raffle-detail-sku">
            {t(`${TP}.skuLabel`, 'SKU')}: {sku}
          </MobileInfoLine>
          <MobileInfoLine data-qa="raffle-detail-colorway">
            {t(`${TP}.colorwayLabel`, 'Colorway')}: {colorway}
          </MobileInfoLine>
          <MobileInfoLine data-qa="raffle-detail-release-date">
            {t(`${TP}.releaseDateLabel`, 'Release Date')}: {formatRaffleDate(releaseDate)}
          </MobileInfoLine>
        </MobileProductInfo>

        <PanelSentinel ref={sentinelRef} />
      </MobileLayout>

      <DesktopLayout>
        <LeftColumn>
          <TitleBlock>
            <TitleSection data-qa="raffle-detail-title">
              <Brand>{brand}</Brand>
              <ProductName>{productName}</ProductName>
            </TitleSection>
            <PriceSection data-qa="raffle-detail-price">{formattedPrice}</PriceSection>
          </TitleBlock>

          {panel && <InlinePanel>{panel}</InlinePanel>}

          <ProductDetails data-qa="raffle-detail-product-info">
            <InfoLine data-qa="raffle-detail-sku">
              {t(`${TP}.skuLabel`, 'SKU')}: {sku}
            </InfoLine>
            <InfoLine data-qa="raffle-detail-colorway">
              {t(`${TP}.colorwayLabel`, 'Colorway')}: {colorway}
            </InfoLine>
            <InfoLine data-qa="raffle-detail-release-date">
              {t(`${TP}.releaseDateLabel`, 'Release Date')}: {formatRaffleDate(releaseDate)}
            </InfoLine>
          </ProductDetails>
        </LeftColumn>

        <CenterColumn>
          {allImages.map((image, i) => renderImage(image, i, productName))}
        </CenterColumn>

        {panel && (
          <RightColumn>
            {panel}
            <ProductInfoLinkWrapper>
              <Link href={`/${raffle.slug}`} passHref legacyBehavior>
                <ProductInfoLink data-qa="raffle-detail-product-info-link">
                  <ProductInfoText>{t(`${TP}.productInfo`, 'Product info')}</ProductInfoText>
                  <ProductInfoChevron>
                    <ChevronDown color={colors.FC2_LIGHT_GREY} />
                  </ProductInfoChevron>
                </ProductInfoLink>
              </Link>
            </ProductInfoLinkWrapper>
          </RightColumn>
        )}
      </DesktopLayout>

      {panel && (
        <MobilePanelSlot
          $collapsed={isCollapsed}
          data-qa="mobile-panel-slot"
        >
          {cloneElement(panel as ReactElement, {
            collapsed: isCollapsed,
            onToggle: () => setIsCollapsed(!isCollapsed),
          })}
        </MobilePanelSlot>
      )}

      <MobileProductInfoLinkWrapper>
        <Link href={`/${raffle.slug}`} passHref legacyBehavior>
          <MobileProductInfoLink data-qa="mobile-product-info-link">
            <MobileProductInfoText>{t(`${TP}.productInfo`, 'Product info')}</MobileProductInfoText>
            <MobileProductInfoChevron>
              <ChevronDown color={colors.FC2_LIGHT_GREY} />
            </MobileProductInfoChevron>
          </MobileProductInfoLink>
        </Link>
      </MobileProductInfoLinkWrapper>

      <MobileBreadcrumbs>
        <Link href="/" passHref legacyBehavior>
          <MobileBreadcrumbLink>{t(`${TP}.breadcrumbHome`, 'Flight Club')}</MobileBreadcrumbLink>
        </Link>
        <MobileSeparator>/</MobileSeparator>
        <Link href="/drawings" passHref legacyBehavior>
          <MobileBreadcrumbLink>{t(`${TP}.breadcrumbRaffles`, 'Drawings')}</MobileBreadcrumbLink>
        </Link>
        <MobileSeparator>/</MobileSeparator>
        <MobileBreadcrumbCurrent>{productName}</MobileBreadcrumbCurrent>
      </MobileBreadcrumbs>
    </PageWrapper>
  )
}

/* ================================
   Layout — prototype: base mobile,
   @media >=1100px  → desktop (prototype uses 1080px; app helper is larger=1100)
   @media >=1280px  → large desktop
   @media >=1920px  → ultra-wide cap (prototype uses 1921; app helper is biggest=1920)
================================ */

const PageWrapper = styled.div`
  width: 100%;
  background: ${colors.FC2_WHITE};
`

/* ── Mobile layout (< 1100px) ── */
const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;

  ${media.larger`
    display: none;
  `}
`

const MobileTitleComponent = styled.div`
  width: 100%;
  padding: 8px 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
`

const MobileTitleTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 392px;
  align-items: center;
`

const MobileBrand = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_RED};
  text-transform: uppercase;
  line-height: 1.2;
`

const MobileProductName = styled.h1`
  ${fonts.IVAR_HEADLINE_LARGE}
  color: ${colors.FC2_RED};
  max-width: 303px;
  margin: 0;
  text-wrap: balance;
`

const MobilePrice = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_BLACK};
  line-height: 1.2;
  margin-bottom: 8px;
`

const MobileImagesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 0 0 40px;
`

const MobileProductInfo = styled.div`
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
`

const MobileInfoLine = styled.p`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_BLACK};
  line-height: 1.2;
  margin: 0;
`

const PanelSentinel = styled.div`
  height: 0;
`

/* ── Sticky breadcrumb bar, desktop only ── */
const Breadcrumbs = styled.nav`
  display: none;

  ${media.larger`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 40px;
    height: 40px;
    font-size: 12px;
    overflow: hidden;
    white-space: nowrap;
  `}
`

const BreadcrumbLink = styled.a`
  ${fonts.BREADCRUMB_LINK}
  cursor: pointer;
`

const Separator = styled.span`
  ${fonts.BREADCRUMB_TEXT}
`

const BreadcrumbCurrent = styled.span`
  ${fonts.BREADCRUMB_TEXT}
  white-space: nowrap;
`

/* ── Desktop grid (hidden on mobile; mobile view is out of scope here) ──
   align-items:start is essential — the grid default (stretch) breaks
   position:sticky on the sidebar children by stretching them to fill
   the grid row instead of letting them scroll independently. */
const DesktopLayout = styled.div`
  display: none;

  ${media.larger`
    display: grid;
    grid-template-columns: 380px 1fr;
    align-items: start;
  `}

  ${media.extraLarge`
    grid-template-columns: 380px 1fr 390px;
  `}
`

/* ── Left sticky sidebar ──
   Flex column with flex-start justification and overflow:hidden.
   Title block sits at top; product details pin to bottom with margin-top:auto.
   At 1280+, top padding grows to 80px (prototype). */
/* Stretches to full grid row height; TitleBlock and ProductDetails
   are independently sticky (top:40 / bottom:40) so each pins 40px
   from its respective viewport edge in both initial and scrolled
   states, regardless of whether the global nav is in view. */
const LeftColumn = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  padding: 40px 0 40px 40px;

  ${media.extraLarge`
    padding: 40px 0 40px 40px;
  `}
`

const TitleBlock = styled.div`
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 80px;
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Brand = styled.div`
  ${fonts.SUBTITLE_3}
  text-transform: uppercase;
  line-height: 1.2;
  color: ${colors.FC2_RED};
`

const ProductName = styled.h1`
  ${fonts.IVAR_HEADLINE_LARGE}
  color: ${colors.FC2_RED};
  max-width: 303px;
  margin: 0;
`

const PriceSection = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_BLACK};
  line-height: 1.2;
`

/* Inline panel slot — visible at medium (1100–1279px), hidden at 1280+
   because RightColumn renders the panel there. */
const InlinePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 220px;
  margin-bottom: 80px;

  ${media.extraLarge`
    display: none;
  `}
`

/* Product details sticky-pinned 40px above the viewport bottom
   (regardless of nav visibility / scroll position). */
const ProductDetails = styled.div`
  position: sticky;
  bottom: 40px;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InfoLine = styled.p`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_BLACK};
  line-height: 1.2;
  margin: 0;
`

/* ── Center scrollable column ── */
const CenterColumn = styled.div`
  padding: 0 26px 160px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  min-width: 0;
  animation: ${entranceAnimation} 600ms ease-out 400ms backwards;

  ${media.extraLarge`
    max-width: 908px;
    margin: 0 auto;
  `}
`

const ProductImageItem = styled.div`
  width: 100%;
  aspect-ratio: 392 / 279;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

const CampaignImageWrap = styled.div`
  width: 100%;
  aspect-ratio: 938 / 667;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
`

const CampaignImageInner = styled.div`
  aspect-ratio: 314 / 407;
  flex: 1 0 0;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const RightColumn = styled.div`
  display: none;

  ${media.extraLarge`
    display: flex;
    flex-direction: column;
    align-self: stretch;
    padding: 40px 40px 40px 0;
    align-items: flex-start;
  `}
`

/* Product info link wrapper - pins to bottom like ProductDetails */
const ProductInfoLinkWrapper = styled.div`
  position: sticky;
  bottom: 40px;
  margin-top: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column;
`

const ProductInfoLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
`

const ProductInfoText = styled.span`
  ${fonts.BODY_TEXT}
  color: ${colors.FC2_LIGHT_GREY};
  line-height: 1.2;
  white-space: nowrap;
`

const ProductInfoChevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  transform: rotate(-90deg);
  padding-top: 1px;
  flex-shrink: 0;
`

/* Mobile panel: sticky at viewport bottom with collapse animation */
const MobilePanelSlot = styled.div<{ $collapsed: boolean }>`
  display: block;
  position: sticky;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  margin: 24px 0 0;
  padding: 0 24px;
  z-index: 20;

  /* Collapse animation via max-height transition */
  transition: margin-bottom 0.3s ease;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    margin-bottom: -200px;
  `}

  ${media.larger`
    display: none;
  `}
`

/* Mobile product info link - positioned 80px above breadcrumb divider */
const MobileProductInfoLinkWrapper = styled.div`
  padding: 0 24px;
  margin-bottom: 80px;
  display: flex;

  ${media.larger`
    display: none;
  `}
`

const MobileProductInfoLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
`

const MobileProductInfoText = styled.span`
  ${fonts.BODY_TEXT}
  color: ${colors.FC2_GREY};
  line-height: 1.2;
  white-space: nowrap;
`

const MobileProductInfoChevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  transform: rotate(-90deg);
  padding-top: 1px;
  flex-shrink: 0;
`

/* Mobile breadcrumbs at bottom of page */
const MobileBreadcrumbs = styled.nav`
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  padding: 24px;
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  background: ${colors.FC2_WHITE};

  ${media.larger`
    display: none;
  `}
`

const MobileBreadcrumbLink = styled.a`
  ${fonts.BREADCRUMB_LINK}
  cursor: pointer;
`

const MobileSeparator = styled.span`
  ${fonts.BREADCRUMB_TEXT}
`

const MobileBreadcrumbCurrent = styled.span`
  ${fonts.BREADCRUMB_TEXT}
  white-space: nowrap;
`
